import { useState } from "react";
import { Link2, Plus, PlusCircle } from "lucide-react";
import { v4 } from "uuid";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Report } from "@/services/backend/htz/report/types/report";
import {
  useHtzReportAddAssessmentMutation,
  useHtzReportAddMicrobialAssessmentWithSampleMutation,
} from "@/services/backend/htz/report/api/report-api";
import { useHtzAirHandlingUnitDataShowQuery } from "@/services/backend/htz/ahu/api/ahu-data-api";
import { AhuComponentSelect } from "@/services/backend/htz/ahu/components/ahu-component-select";
import { ReportMicrobialSampleLinkDialog } from "@/services/backend/htz/report/components/report-microbial-sample-link-dialog";
import { generateSampleNumber } from "@/services/backend/samples/types/sample";
import { Kind } from "@/services/backend/samples/types/kind";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";

/**
 * ReportMicrobialAssessmentAddDropdown adds a microbial assessment for one
 * component of the report's air handling unit.
 *
 * A microbial assessment reports the result of a sample, so a sample is
 * created along with it - one of the five kinds the laboratory analyses.
 * An already existing sample can be linked instead, see
 * {@link ReportMicrobialSampleLinkDialog}.
 */
export function ReportMicrobialAssessmentAddDropdown({
  report,
  disabled,
}: {
  report: Report;
  disabled?: boolean;
}) {
  const [componentId, setComponentId] = useState<UUID>("");
  const [assessmentId, setAssessmentId] = useState<UUID>("");

  const { data: ahuData } = useHtzAirHandlingUnitDataShowQuery(
    { id: report.ahuDataId ?? "" },
    { skip: !report.ahuDataId },
  );

  const [add, { isLoading: addIsLoading, error: addError }] =
    useHtzReportAddAssessmentMutation();
  const [
    addWithSample,
    { isLoading: addWithSampleIsLoading, error: addWithSampleError },
  ] = useHtzReportAddMicrobialAssessmentWithSampleMutation();

  const isLoading = addIsLoading || addWithSampleIsLoading;

  const component = ahuData?.components.find(
    (c) => c.componentId === componentId,
  );
  const location = ahuData?.designation ?? "";
  const spot = component?.name ?? "";

  // The backend creates the sample with the defaults of the company's
  // base data and adds the assessment referencing it in one transaction.
  const addWithNewSample = (kind: SampleKind) => {
    if (isLoading || componentId === "") {
      return;
    }

    addWithSample({
      id: report.id,
      assessmentId: v4(),
      componentId,
      sampleId: v4(),
      sampleKind:
        kind === "warmWater" || kind === "coldWater" ? Kind.Water : kind,
      waterKind:
        kind === "warmWater"
          ? "warm"
          : kind === "coldWater"
            ? "cold"
            : undefined,
      number: generateSampleNumber(),
      location,
      spot,
    })
      .unwrap()
      .then(() => {
        toast.success(t("Probe angelegt und Beurteilung hinzugefügt"));
      })
      .catch(() => {
        // errors are surfaced via RTKQueryErrorAlert
      });
  };

  // Linking an existing sample needs an assessment to link it to, but it
  // is only created once the dialog confirms - see onBeforeLink below.
  // Creating it up front would leave a blank assessment on the report
  // whenever the user dismisses the dialog.
  const addForLinking = () => {
    if (isLoading || componentId === "") {
      return;
    }

    setAssessmentId(v4());
  };

  // A microbial assessment belongs to a component, so there is nothing to
  // add before an air handling unit is assigned.
  if (!report.ahuId) {
    return null;
  }

  return (
    <div className="grid justify-items-end gap-2">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button size="sm" disabled={disabled || isLoading}>
            <PlusCircle />
            {t("Probe")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>{t("Komponente")}</DropdownMenuLabel>
          <DropdownMenuItem
            className="focus:bg-transparent"
            onSelect={(e) => e.preventDefault()}
          >
            <AhuComponentSelect
              ahuDataId={report.ahuDataId!}
              componentId={componentId}
              onComponentIdChange={setComponentId}
              disabled={disabled}
              className="w-full"
            />
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t("Neue Probe")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {sampleKinds.map((kind) => (
            <DropdownMenuItem
              key={kind.value}
              onClick={() => addWithNewSample(kind.value)}
              disabled={componentId === ""}
            >
              <Plus />
              <span>{kind.label}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={addForLinking}
            disabled={componentId === ""}
          >
            <Link2 />
            <span>{t("Bestehende Probe verknüpfen")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <RTKQueryErrorAlert error={addError || addWithSampleError} />
      {assessmentId !== "" && (
        <ReportMicrobialSampleLinkDialog
          reportId={report.id}
          assessmentId={assessmentId}
          onBeforeLink={() =>
            add({
              id: report.id,
              type: "microbial",
              assessmentId,
              componentId,
            }).unwrap()
          }
          open
          onOpenChange={(open) => {
            if (!open) {
              setAssessmentId("");
            }
          }}
        />
      )}
    </div>
  );
}

type SampleKind =
  | Kind.AirMicrobial
  | Kind.SurfaceMicrobial
  | Kind.DustDensity
  | "coldWater"
  | "warmWater";

// Water is one kind for the backend, but the two variants carry different
// measurements, so they are offered separately.
const sampleKinds: { value: SampleKind; label: string }[] = [
  { value: Kind.AirMicrobial, label: t("Luftkeimprobe") },
  { value: Kind.SurfaceMicrobial, label: t("Oberflächenkeimprobe") },
  { value: Kind.DustDensity, label: t("Staubdichtemessung") },
  { value: "coldWater", label: t("Kaltwasserprobe") },
  { value: "warmWater", label: t("Warmwasserprobe") },
];
